import type { VisualPayload, FieldResearchTask, FieldResearchReward } from "../../types";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";

interface Props { payload: VisualPayload }

// Sub-grouping accent palette per task type.  Catch / battle stand
// out (the most actionable for daily play); hatch / throw / other
// stay muted.
const TYPE_ACCENT: Record<string, { border: string; label: string; pill: string }> = {
  catch: {
    border: "border-emerald-500/25",
    label: "text-emerald-300",
    pill: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
  },
  battle: {
    border: "border-rose-500/25",
    label: "text-rose-300",
    pill: "bg-rose-500/10 border-rose-500/20 text-rose-300",
  },
  raid: {
    border: "border-amber-500/25",
    label: "text-amber-300",
    pill: "bg-amber-500/10 border-amber-500/20 text-amber-300",
  },
  throw: {
    border: "border-blue-500/20",
    label: "text-blue-300",
    pill: "bg-blue-500/10 border-blue-500/20 text-blue-300",
  },
  hatch: {
    border: "border-pink-500/20",
    label: "text-pink-300",
    pill: "bg-pink-500/10 border-pink-500/20 text-pink-300",
  },
  walk: {
    border: "border-sky-500/20",
    label: "text-sky-300",
    pill: "bg-sky-500/10 border-sky-500/20 text-sky-300",
  },
  friend: {
    border: "border-purple-500/20",
    label: "text-purple-300",
    pill: "bg-purple-500/10 border-purple-500/20 text-purple-300",
  },
  other: {
    border: "border-white/15",
    label: "text-white/55",
    pill: "bg-white/5 border-white/15 text-white/60",
  },
};

const FALLBACK_ACCENT = TYPE_ACCENT.other;

function ShinySparkle() {
  return (
    <span
      aria-label="shiny available"
      title="Shiny available"
      className="text-yellow-300 text-[11px]"
    >
      ✦
    </span>
  );
}

function RewardChip({ reward }: { reward: FieldResearchReward }) {
  if (!reward.name) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] bg-white/5 border border-white/10 text-white/80 px-1.5 py-0.5 rounded">
      <PokemonSprite src={reward.image ?? undefined} alt={reward.name} size="sm" />
      <span>{reward.name}</span>
      {reward.can_be_shiny && <ShinySparkle />}
      {reward.cp_min != null && reward.cp_max != null && (
        <span className="text-[9px] text-white/40 tabular-nums">
          {reward.cp_min.toLocaleString()}–{reward.cp_max.toLocaleString()}
        </span>
      )}
    </span>
  );
}

/**
 * "What field research is available?" — current PokéStop research
 * tasks grouped by task type (catch / throw / battle / hatch / ...).
 *
 * Each task row shows the task text, then a chip per reward Pokémon
 * with shiny indicator and CP range.  We intentionally don't sort
 * within a task-type bucket — backend already orders by text so the
 * UI reflects the same shape across sessions (helps muscle memory).
 *
 * NOTE: this is Field Research (rotating PokéStop spin tasks), not
 * Research Breakthrough (the monthly milestone encounter).
 * ScrapedDuck doesn't publish Breakthrough data.
 *
 * Source: LeekDuck via ScrapedDuck (attribution footer required
 * per ScrapedDuck's licence terms).
 *
 * Phase 3.
 */
export default function FieldResearchCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const summarySec = sections.find((s) => s.section === "research_summary");
  const byTypeSec = sections.find((s) => s.section === "research_tasks_by_type");
  const byType =
    (byTypeSec?.by_task_type as Record<string, FieldResearchTask[]> | undefined) ?? {};
  const typeCounts = (summarySec?.task_type_counts as Record<string, number> | undefined) ?? {};
  const totalTasks = (summarySec?.total_tasks as number | undefined) ?? 0;
  const shinyRewards = (summarySec?.shiny_rewards as number | undefined) ?? 0;

  const typeEntries = Object.entries(byType);

  if (typeEntries.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-amber-500/20">
        <p className="text-sm text-white/55 py-2">No current Field Research data available.</p>
      </CardShell>
    );
  }

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-amber-500/30"
    >
      {/* Top strip: totals + per-task-type chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-white/[0.06]">
        <span className="text-sm text-white/70">
          {totalTasks} active tasks
        </span>
        {shinyRewards > 0 && (
          <span className="text-[11px] text-yellow-300/80 flex items-center gap-0.5">
            <ShinySparkle /> {shinyRewards} shiny-capable rewards
          </span>
        )}
        <span className="text-white/20" aria-hidden="true">·</span>
        {Object.entries(typeCounts).map(([taskType, n]) => {
          const accent = TYPE_ACCENT[taskType] ?? FALLBACK_ACCENT;
          return (
            <span
              key={taskType}
              className={`text-[10px] px-1.5 py-0.5 rounded border capitalize ${accent.pill}`}
            >
              {taskType}: {n}
            </span>
          );
        })}
      </div>

      {/* Per-task-type sections */}
      <div className="space-y-4">
        {typeEntries.map(([taskType, tasks]) => {
          const accent = TYPE_ACCENT[taskType] ?? FALLBACK_ACCENT;
          return (
            <section key={taskType}>
              <h4 className={`text-[11px] font-semibold uppercase tracking-widest mb-2 ${accent.label}`}>
                {taskType} ({tasks.length})
              </h4>
              <ul className="space-y-2">
                {tasks.map((t, i) => (
                  <li
                    key={i}
                    className={`flex flex-col gap-1.5 text-xs border-l-2 pl-2.5 py-1 ${accent.border}`}
                  >
                    <p className="text-white/85 leading-snug break-words min-w-0">
                      {t.text}
                    </p>
                    {t.rewards && t.rewards.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {t.rewards.map((r, ri) => (
                          <RewardChip key={ri} reward={r} />
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {/* Attribution footer — required per ScrapedDuck licence */}
      <div className="mt-4 pt-3 border-t border-white/[0.06]">
        <p className="text-[11px] text-white/35">
          Research data from{" "}
          <a
            href="https://leekduck.com/research/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/70 hover:text-amber-300 transition-colors"
          >
            LeekDuck
          </a>{" "}
          via{" "}
          <a
            href="https://github.com/bigfoott/ScrapedDuck"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/70 hover:text-amber-300 transition-colors"
          >
            ScrapedDuck
          </a>
        </p>
      </div>
    </CardShell>
  );
}
